import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import {
  FileText, Upload, Plus, Trash, PencilSimple, Check, X,
  CircleNotch, Brain, ArrowRight, FileDoc, FilePdf, TextT, Microphone, Target,
} from "@phosphor-icons/react";
import { API, getAuthHeaders } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const TYPE_ICONS = { file: FileDoc, text: TextT };

const TONE_PRESETS = [
  "Bold & Confident — no-nonsense, straight talk",
  "Warm & Friendly — conversational, like chatting with a friend",
  "Professional & Polished — authoritative, buttoned-up",
  "Playful & Witty — light, fun, uses humor",
  "Inspiring & Motivational — uplifting, push-you-forward energy",
  "Edgy & Raw — unfiltered, real talk, provocative",
];

const BrandKnowledgeBase = () => {
  const { user, refreshUser } = useAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [mode, setMode] = useState(null);
  const [editId, setEditId] = useState(null);
  const [title, setTitle] = useState("");
  const [content, setContent] = useState("");
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [toneOfVoice, setToneOfVoice] = useState(user?.tone_of_voice || "");
  const [savingTone, setSavingTone] = useState(false);
  const [toneSaved, setToneSaved] = useState(false);

  // North Star state
  const existingNS = user?.north_star || {};
  const [nsOffer, setNsOffer] = useState(existingNS.primary_offer || "");
  const [nsLink, setNsLink] = useState(existingNS.offer_link || "");
  const [nsCta, setNsCta] = useState(existingNS.primary_cta || "");
  const [nsAltCtas, setNsAltCtas] = useState((existingNS.alt_ctas || []).join("\n"));
  const [nsMetric, setNsMetric] = useState(existingNS.conversion_metric || "");
  const [savingNS, setSavingNS] = useState(false);
  const [nsSaved, setNsSaved] = useState(false);

  useEffect(() => {
    if (user?.north_star) {
      setNsOffer(user.north_star.primary_offer || "");
      setNsLink(user.north_star.offer_link || "");
      setNsCta(user.north_star.primary_cta || "");
      setNsAltCtas((user.north_star.alt_ctas || []).join("\n"));
      setNsMetric(user.north_star.conversion_metric || "");
    }
  }, [user]);

  const saveNorthStar = async () => {
    setSavingNS(true);
    try {
      const altCtas = nsAltCtas
        .split("\n")
        .map((x) => x.trim())
        .filter(Boolean);
      await axios.patch(`${API}/auth/onboard`, {
        north_star: {
          primary_offer: nsOffer.trim(),
          offer_link: nsLink.trim(),
          primary_cta: nsCta.trim(),
          alt_ctas: altCtas,
          conversion_metric: nsMetric.trim(),
        },
      }, { headers: { ...getAuthHeaders(), "Content-Type": "application/json" } });
      await refreshUser();
      setNsSaved(true);
      setTimeout(() => setNsSaved(false), 4000);
    } catch (err) {
      alert(err.response?.data?.detail || "Failed to save North Star");
    } finally {
      setSavingNS(false);
    }
  };

  useEffect(() => {
    if (user?.tone_of_voice) setToneOfVoice(user.tone_of_voice);
  }, [user]);

  const fetchItems = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/brand-kb`, { headers: getAuthHeaders() });
      setItems(res.data.items || []);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchItems(); }, [fetchItems]);

  const handleUploadFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append("file", file);
      const res = await axios.post(`${API}/brand-kb/upload`, formData, {
        headers: { ...getAuthHeaders(), "Content-Type": "multipart/form-data" },
      });
      setSaveSuccess(true);
      setTimeout(() => setSaveSuccess(false), 4000);
      fetchItems();
      const filled = res.data?.autofilled_north_star;
      if (filled && Object.keys(filled).length > 0) {
        // Refresh auth user so North Star form fields repopulate from server
        await refreshUser();
        const labels = Object.keys(filled).map((k) => k.replace(/_/g, " ")).join(", ");
        alert(`Auto-filled your North Star: ${labels}. You can edit these anytime below.`);
      }
    } catch (err) {
      alert(err.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  const handleSaveText = async () => {
    if (!title.trim() || !content.trim()) return;
    setSaving(true);
    try {
      let autofilledFromServer = null;
      if (editId) {
        await axios.put(`${API}/brand-kb/${editId}`, { title, content }, { headers: getAuthHeaders() });
      } else {
        const res = await axios.post(`${API}/brand-kb/text`, { title, content }, { headers: getAuthHeaders() });
        autofilledFromServer = res.data?.autofilled_north_star;
      }
      setMode(null);
      setEditId(null);
      setTitle("");
      setContent("");
      setSaveSuccess(true);
      setTimeout(() => setSaveSuccess(false), 4000);
      fetchItems();
      if (autofilledFromServer && Object.keys(autofilledFromServer).length > 0) {
        await refreshUser();
        const labels = Object.keys(autofilledFromServer).map((k) => k.replace(/_/g, " ")).join(", ");
        alert(`Auto-filled your North Star: ${labels}. You can edit these anytime below.`);
      }
    } catch (err) {
      alert(err.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const handleEdit = async (id) => {
    try {
      const res = await axios.get(`${API}/brand-kb/${id}`, { headers: getAuthHeaders() });
      setTitle(res.data.title || "");
      setContent(res.data.content || "");
      setEditId(id);
      setMode("add-text");
    } catch (e) { console.error(e); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this knowledge entry?")) return;
    try {
      await axios.delete(`${API}/brand-kb/${id}`, { headers: getAuthHeaders() });
      fetchItems();
    } catch (e) { console.error(e); }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16">
        <CircleNotch size={28} className="animate-spin text-[#E91E8C]" />
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto space-y-6" data-testid="brand-kb-section">
      <div>
        <h2 className="text-2xl font-bold text-zinc-900 flex items-center gap-2">
          <Brain size={24} weight="duotone" className="text-[#E91E8C]" />
          Brand Knowledge Base
        </h2>
        <p className="text-zinc-500 text-sm mt-1">
          Upload documents or add text about your brand — goals, offers, CTAs, target audience. The AI uses this to create hyper-personalized content.
        </p>
      </div>

      {/* Info card */}
      <div className="bg-[#E91E8C]/5 border border-[#E91E8C]/20 rounded-xl p-4">
        <p className="text-sm text-zinc-700">
          <strong className="text-[#E91E8C]">How it works:</strong> Everything you add here is fed to the AI when generating scripts, topics, hooks, and content ideas. The more detail you provide about your brand, the more on-point and personalized your content will be.
        </p>
      </div>

      {/* Success banner */}
      {(saveSuccess || toneSaved) && (
        <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-4 flex items-center gap-3" data-testid="kb-save-success">
          <Check size={20} weight="bold" className="text-emerald-500 shrink-0" />
          <div>
            <p className="text-sm font-semibold text-emerald-700">{toneSaved ? "Tone of voice saved!" : "Brand knowledge updated!"}</p>
            <p className="text-xs text-emerald-600">Your AI-generated topics, scripts, and content ideas will now reflect this update.</p>
          </div>
        </div>
      )}

      {/* Tone of Voice - Separate Section */}
      <div className="card p-5 space-y-4 border border-zinc-200">
        <div className="flex items-center gap-2 mb-1">
          <Microphone size={18} weight="duotone" className="text-[#E91E8C]" />
          <h3 className="text-sm font-bold text-zinc-900">Tone of Voice</h3>
        </div>
        <p className="text-xs text-zinc-500 -mt-2">Set your brand's tone — this is applied to ALL AI-generated content across the entire platform.</p>

        <div className="flex flex-wrap gap-2">
          {TONE_PRESETS.map((preset) => (
            <button
              key={preset}
              onClick={() => setToneOfVoice(preset)}
              data-testid={`tone-preset-${preset.split("—")[0].trim().toLowerCase().replace(/\s|&/g, "-")}`}
              className={`px-3 py-1.5 rounded-lg text-xs font-medium border transition-all ${
                toneOfVoice === preset
                  ? "border-[#E91E8C] bg-[#E91E8C]/10 text-[#E91E8C]"
                  : "border-zinc-200 text-zinc-500 hover:border-zinc-300"
              }`}
            >
              {preset.split("—")[0].trim()}
            </button>
          ))}
        </div>

        <textarea
          value={toneOfVoice}
          onChange={(e) => setToneOfVoice(e.target.value)}
          placeholder="Describe your desired tone of voice... e.g. 'Confident but approachable, like a smart friend giving advice. Use humor sparingly. Always end with encouragement.'"
          rows={3}
          data-testid="tone-of-voice-input"
          className="input-field text-sm resize-none"
        />
        <div className="flex items-center gap-3">
          <button
            onClick={async () => {
              setSavingTone(true);
              try {
                await axios.patch(`${API}/auth/onboard`, { tone_of_voice: toneOfVoice }, { headers: { ...getAuthHeaders(), "Content-Type": "application/json" } });
                await refreshUser();
                setToneSaved(true);
                setTimeout(() => setToneSaved(false), 4000);
              } catch (err) {
                alert("Failed to save tone");
              } finally {
                setSavingTone(false);
              }
            }}
            disabled={savingTone}
            data-testid="save-tone-btn"
            className="px-5 py-2.5 bg-[#E91E8C] text-white rounded-xl text-sm font-semibold hover:bg-[#d4177d] disabled:opacity-50 transition-all flex items-center gap-2"
          >
            {savingTone ? <CircleNotch size={14} className="animate-spin" /> : <Check size={14} weight="bold" />}
            {savingTone ? "Saving..." : "Save Tone"}
          </button>
          {toneOfVoice && (
            <button onClick={() => setToneOfVoice("")} className="text-xs text-zinc-400 hover:text-zinc-600">Clear</button>
          )}
        </div>
      </div>

      {/* North Star - Conversion framework */}
      <div className="card p-5 space-y-4 border-2 border-[#E91E8C]/30 bg-gradient-to-br from-[#E91E8C]/5 via-pink-50/50 to-white" data-testid="north-star-card">
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 rounded-xl bg-[#E91E8C]/15 flex items-center justify-center flex-shrink-0">
            <Target size={20} weight="duotone" className="text-[#E91E8C]" />
          </div>
          <div className="flex-1">
            <h3 className="text-sm font-bold text-zinc-900">North Star — Your Conversion Framework</h3>
            <p className="text-xs text-zinc-500 mt-0.5 leading-relaxed">
              Set this once and every script, hook, LinkedIn post, and series episode the AI writes will be anchored toward this goal.
              No more generic content — everything drives toward a specific conversion.
            </p>
          </div>
        </div>

        {nsSaved && (
          <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-3 flex items-center gap-2" data-testid="ns-save-success">
            <Check size={16} weight="bold" className="text-emerald-500" />
            <p className="text-sm font-semibold text-emerald-700">North Star saved! Applied across every AI tool.</p>
          </div>
        )}

        <div className="space-y-3">
          <div>
            <label className="block text-xs text-zinc-500 font-medium mb-1">Primary offer</label>
            <input
              type="text"
              value={nsOffer}
              onChange={(e) => setNsOffer(e.target.value)}
              placeholder="e.g., The Branding Bar — AI content platform for personal brands"
              data-testid="ns-offer-input"
              className="input-field text-sm"
            />
          </div>
          <div>
            <label className="block text-xs text-zinc-500 font-medium mb-1">Offer link</label>
            <input
              type="url"
              value={nsLink}
              onChange={(e) => setNsLink(e.target.value)}
              placeholder="https://thebrandingbarlab.com"
              data-testid="ns-link-input"
              className="input-field text-sm"
            />
          </div>
          <div>
            <label className="block text-xs text-zinc-500 font-medium mb-1">Conversion goal (what should viewers DO?)</label>
            <input
              type="text"
              value={nsMetric}
              onChange={(e) => setNsMetric(e.target.value)}
              placeholder="e.g., Sign up for free trial, Buy the ebook, Join waitlist"
              data-testid="ns-metric-input"
              className="input-field text-sm"
            />
          </div>
          <div>
            <label className="block text-xs text-zinc-500 font-medium mb-1">Primary call-to-action (used verbatim in every script)</label>
            <input
              type="text"
              value={nsCta}
              onChange={(e) => setNsCta(e.target.value)}
              placeholder="e.g., Comment AI and I'll DM you the free trial link to The Branding Bar"
              data-testid="ns-cta-input"
              className="input-field text-sm"
            />
          </div>
          <div>
            <label className="block text-xs text-zinc-500 font-medium mb-1">
              Alternate CTAs (one per line — shown as dropdown options in Script Studio)
            </label>
            <textarea
              value={nsAltCtas}
              onChange={(e) => setNsAltCtas(e.target.value)}
              placeholder={"Link in bio for the free trial\nComment BRAND for early access\nFollow for more AI content creation tips"}
              rows={3}
              data-testid="ns-alt-ctas-input"
              className="input-field text-sm resize-none"
            />
          </div>
        </div>

        <div className="flex items-center gap-3">
          <button
            onClick={saveNorthStar}
            disabled={savingNS}
            data-testid="save-north-star-btn"
            className="px-5 py-2.5 bg-[#E91E8C] text-white rounded-xl text-sm font-semibold hover:bg-[#d4177d] disabled:opacity-50 transition-all flex items-center gap-2"
          >
            {savingNS ? <CircleNotch size={14} className="animate-spin" /> : <Target size={14} weight="fill" />}
            {savingNS ? "Saving..." : "Save North Star"}
          </button>
          <p className="text-[11px] text-zinc-400 leading-tight">
            Applied platform-wide: Script Studio, Series Studio, LinkedIn Engine, Auto Create, Bulk Create, Hooks.
          </p>
        </div>
      </div>

      {/* Action buttons */}
      {!mode && (
        <div className="flex gap-3">
          <label className={`flex items-center gap-2 px-4 py-2.5 bg-white border-2 border-dashed border-zinc-300 rounded-xl text-sm font-medium text-zinc-600 hover:border-[#E91E8C] hover:text-[#E91E8C] cursor-pointer transition-all ${uploading ? "opacity-50 pointer-events-none" : ""}`}>
            {uploading ? <CircleNotch size={16} className="animate-spin" /> : <Upload size={16} />}
            {uploading ? "Uploading..." : "Upload Document"}
            <input type="file" accept=".txt,.pdf,.docx,.md" onChange={handleUploadFile} className="hidden" data-testid="kb-file-upload" />
          </label>
          <button
            onClick={() => { setMode("add-text"); setEditId(null); setTitle(""); setContent(""); }}
            data-testid="kb-add-text-btn"
            className="flex items-center gap-2 px-4 py-2.5 bg-[#E91E8C] text-white rounded-xl text-sm font-semibold hover:bg-[#d4177d] transition-all"
          >
            <Plus size={16} weight="bold" /> Add Text
          </button>
        </div>
      )}

      {/* Add/Edit Text Form */}
      {mode === "add-text" && (
        <div className="card p-5 space-y-4 border-2 border-[#E91E8C]/20">
          <h3 className="text-sm font-bold text-zinc-900">{editId ? "Edit Entry" : "Add Brand Knowledge"}</h3>
          <div>
            <label className="block text-xs text-zinc-500 font-medium mb-1">Title</label>
            <input
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="e.g. Our Core Offers, Target Audience, Brand Voice Guide"
              data-testid="kb-title-input"
              className="input-field text-sm"
            />
          </div>
          <div>
            <label className="block text-xs text-zinc-500 font-medium mb-1">Content</label>
            <textarea
              value={content}
              onChange={(e) => setContent(e.target.value)}
              placeholder={"Describe your brand in detail...\n\nExamples:\n- What products/services do you offer?\n- Who is your target audience?\n- What are your main CTAs (calls-to-action)?\n- What tone and energy should your content have?\n- What are your business goals?\n- What topics should AI focus on?"}
              rows={8}
              data-testid="kb-content-input"
              className="input-field text-sm resize-none"
            />
            <p className="text-[10px] text-zinc-400 mt-1">{content.length.toLocaleString()} / 50,000 characters</p>
          </div>
          <div className="flex gap-2">
            <button
              onClick={handleSaveText}
              disabled={saving || !title.trim() || !content.trim()}
              data-testid="kb-save-btn"
              className="flex items-center gap-2 px-5 py-2.5 bg-[#E91E8C] text-white rounded-xl text-sm font-semibold hover:bg-[#d4177d] disabled:opacity-50 transition-all"
            >
              {saving ? <CircleNotch size={16} className="animate-spin" /> : <Check size={16} weight="bold" />}
              {saving ? "Saving..." : editId ? "Update" : "Save"}
            </button>
            <button
              onClick={() => { setMode(null); setEditId(null); setTitle(""); setContent(""); }}
              className="px-4 py-2.5 bg-zinc-100 text-zinc-600 rounded-xl text-sm font-medium hover:bg-zinc-200 transition-all"
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* Items list */}
      {items.length === 0 && !mode ? (
        <div className="text-center py-12">
          <Brain size={40} className="text-zinc-300 mx-auto mb-3" />
          <p className="text-zinc-500 text-sm mb-1">No brand knowledge added yet</p>
          <p className="text-zinc-400 text-xs">Upload a document or add text to train the AI on your brand</p>
        </div>
      ) : (
        <div className="space-y-2">
          {items.length > 0 && <p className="text-xs font-bold uppercase tracking-wider text-zinc-400">{items.length} entries</p>}
          {items.map((item) => {
            const Icon = TYPE_ICONS[item.type] || FileText;
            return (
              <div key={item.id} className="card p-4 flex items-start gap-3" data-testid={`kb-item-${item.id}`}>
                <div className="w-9 h-9 rounded-lg bg-zinc-100 flex items-center justify-center shrink-0 mt-0.5">
                  <Icon size={18} className="text-[#E91E8C]" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold text-zinc-900">{item.title}</p>
                  <p className="text-xs text-zinc-400 mt-0.5">{item.char_count?.toLocaleString()} chars &middot; {item.type === "file" ? "Uploaded file" : "Text entry"} &middot; {new Date(item.created_at).toLocaleDateString()}</p>
                  <p className="text-xs text-zinc-500 mt-1 line-clamp-2">{item.preview}</p>
                </div>
                <div className="flex items-center gap-1 shrink-0">
                  <button onClick={() => handleEdit(item.id)} className="p-1.5 text-zinc-400 hover:text-[#E91E8C] transition-all" data-testid={`kb-edit-${item.id}`}>
                    <PencilSimple size={14} />
                  </button>
                  <button onClick={() => handleDelete(item.id)} className="p-1.5 text-zinc-400 hover:text-red-500 transition-all" data-testid={`kb-delete-${item.id}`}>
                    <Trash size={14} />
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default BrandKnowledgeBase;
